import React from "react";
import { Link, useHistory } from "react-router-dom";
import { Card, Box, Typography, Grid, Stack } from "@mui/material";
import { Gamepad2 } from "lucide-react";
import {
  CasinoOutlined as RevolverIcon,
  SportsEsportsOutlined as MineIcon,
  AutoAwesomeOutlined as MRLIcon,
  RocketLaunchOutlined as MissileIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonButton } from "../../../../Components/ui";
import { Path } from "../../../../../static/Constants";

const ENGINES = [
  {
    title: "MegaBET",
    subtitle: "Raffle & Rollover Jackpots",
    description:
      "Classic multi-number matrix drawings with automatic jackpot rollovers, tiered prize allocations, and verifiable Chainlink VRF resolutions.",
    icon: MRLIcon,
    accentColor: colors.emerald[600],
    bgColor: colors.emerald[50],
    borderColor: colors.emerald[200],
    tag: "High Stakes & Rollover",
    path: Path.allLotteries,
  },
  {
    title: "SpinBET",
    subtitle: "Roulette Wheel Lottery",
    description:
      "Interactive 1-to-12 roulette wheel lottery with instant on-chain spin execution, high frequency rounds, and instant smart contract payouts.",
    icon: RevolverIcon,
    accentColor: colors.primary.main,
    bgColor: colors.blue[50],
    borderColor: colors.blue[200],
    tag: "Instant Spin & Win",
    path: Path.Spinnerlottery,
  },
  {
    title: "MineBET",
    subtitle: "Single Pick Multipliers",
    description:
      "Fast-paced single-selection lottery where players pick their lucky mine slot and the draw resolves automatically as soon as capacity is filled.",
    icon: MineIcon,
    accentColor: colors.purple[600],
    bgColor: colors.purple[50],
    borderColor: colors.purple[200],
    tag: "Instant Round Fill",
    path: Path.allminelottery,
  },
  {
    title: "RocketBET",
    subtitle: "Speed Blast Lottery",
    description:
      "Multi-stage blast lottery featuring dynamic multiplier targets, speed rounds, and instant on-chain winning disbursements.",
    icon: MissileIcon,
    accentColor: colors.amber[600],
    bgColor: colors.amber[50],
    borderColor: colors.amber[200],
    tag: "High Multipliers",
    path: Path.allmissilelottery,
  },
];

const AboutGameEngines = () => {
  const history = useHistory();

  return (
    <Box sx={{ mb: 6 }}>
      {/* Section Header Card */}
      <Card
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          mb: 3.5,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: borderRadiusPx.lg,
                bgcolor: colors.purple[50],
                color: colors.purple[600],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(124, 58, 237, 0.12)",
              }}
            >
              <Gamepad2 size={22} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: colors.slate[900],
                  fontSize: "1.25rem",
                }}
              >
                Proprietary Game Engines
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.slate[500], fontSize: "0.875rem" }}
              >
                Explore our versatile lineup of on-chain lottery formats
                designed for every play style
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Card>

      {/* Engines Grid */}
      <Grid container spacing={3}>
        {ENGINES.map((engine, idx) => {
          const IconComp = engine.icon;
          return (
            <Grid item xs={12} sm={6} lg={3} key={idx}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: borderRadiusPx.xl,
                  backgroundColor: colors.dashboard.cardBg,
                  border: `1px solid ${colors.dashboard.cardBorder}`,
                  boxShadow: customShadows.cardFlat,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: engine.accentColor,
                    boxShadow: customShadows.cardHover,
                    transform: "translateY(-3px)",
                  },
                }}
              >
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: borderRadiusPx.lg,
                        bgcolor: engine.bgColor,
                        color: engine.accentColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComp sx={{ fontSize: 24 }} />
                    </Box>
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.35,
                        borderRadius: borderRadiusPx.full,
                        bgcolor: engine.bgColor,
                        color: engine.accentColor,
                        fontSize: "0.68rem",
                        fontWeight: 800,
                        textTransform: "uppercase",
                      }}
                    >
                      {engine.tag}
                    </Box>
                  </Stack>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: colors.slate[900],
                      mb: 0.5,
                      fontSize: "1.05rem",
                    }}
                  >
                    {engine.title}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: engine.accentColor,
                      fontWeight: 700,
                      mb: 1.5,
                      display: "block",
                    }}
                  >
                    {engine.subtitle}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: colors.slate[600], lineHeight: 1.6, mb: 2.5 }}
                  >
                    {engine.description}
                  </Typography>
                </Box>

                <CommonButton
                  variant="outline"
                  size="small"
                  component={Link}
                  to={engine.path}
                  onClick={() => history.push(engine.path)}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  sx={{ width: "100%", textDecoration: "none" }}
                >
                  Play Game
                </CommonButton>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AboutGameEngines;
